import { Container, Flex, Heading, Text, Image, Box, Link, SimpleGrid } from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import tom from "/public/assets/images/tom.jpg"
import werner from "/public/assets/images/werner.jpg"
import zeitungen from "/public/assets/images/zeitungen.jpg"
import logo from "../../../public/assets/images/logo.svg"
import hdLogo from "../../../public/assets/images/herrmann_druck.svg"
import verlagLogo from "../../../public/assets/images/herrmann_verlag_logo.svg"
import { useQuery } from "@tanstack/react-query"
import { getImprint } from "../../utils"
import NotFound from "../../components/Common/NotFound"
import ReactPlayer from "react-player"

export const Route = createFileRoute("/_layout/ueber-uns")({
  component: AboutUs,
  notFoundComponent: NotFound,
})


function AboutUs() {
    const {data: imprint} = useQuery({
        ...getImprint()
    })

    return (
        <Container p={{md: 0}}>
            <Heading size="5xl" color="main" mb={8}>Wie entstand die Wochen-Zeitung</Heading>
            <Text mb={8}>
                Die Welt der Medien dreht sich rasant. 
                In der Schweiz buhlen heute nur noch sechs, sieben grosse Medienhäuser wie Ringier, NZZ oder Tamedia um die Gunst der Leser und Hörer. 
                Daneben füllen einige Medienhäuschen die Lücken der Grossen auf. 
                Die dominierenden Konzerne kaufen Titel und vereinen lokale Zeitungen und Radios. 
                Dass solche Ereignisse nicht neu sind, zeigt sich am Beispiel der «Wochen-Zeitung»: Wegen einer Fusion wurde sie ins Leben gerufen.
            </Text>

            <Heading size="lg" color="main" mb={2}>Die Emmentaler verloren ihr Blatt</Heading>
            <Text mb={8}>
                Das «Emmenthaler Blatt» ging in den 70er-Jahren die Hochzeit mit den «Tagesnachrichten» aus Münsingen ein, 1979 vereinten sich die beiden dann mit dem «Berner Tagblatt». 
                Die Emmentaler Bevölkerung verlor damit ihre eigene Tageszeitung. Für den Langnauer Werner Herrmann war dies Grund genug, eine eigene Emmentaler Zeitung zu gründen. 
                Am 13. November 1980 ist das erste Exemplar der «Wochen-Zeitung» erschienen. 
                Schon damals gehörte ein Teil des Amtes Entlebuch zum Gebiet, deshalb die Bezeichnung «Emmental und Entlebuch». 
                Die neue Zeitung erschien einmal wöchentlich und sie war und ist gratis. 
                Gratiszeitungen waren 1980 selten, sind heute unter anderem dank der Pendlerzeitungen ein fester Bestandteil der Medienlandschaft.
            </Text>

            <Flex 
                direction={{base: "column", md: "row"}} 
                spaceX={{base: 0, md: 8}} 
                spaceY={{base: 8, md: 0}}
                mb={4} 
                textAlign="center"
                justifyContent={{base: "center", md: "space-around"}} 
            >
                <Box w={{base: "full", md: "30%"}}>
                    <Image src={tom} />
                    <Text color="main" mt={2}>Tom Herrmann</Text>
                </Box>
                
                <Box w={{base: "full", md: "30%"}}>
                    <Image src={werner} />
                    <Text color="main" mt={2}>Werner Herrmann</Text>
                </Box>
            </Flex>

            <Text mb={8}>
                Werner Herrmann leitete das Unternehmen bis Mitte 2011. 
                Dann übergab er die Geschäftsführung an seinen Sohn Thomas. 
                Werner Herrmann übt weiterhin das Amt des Verwaltungsratspräsidenten aus.
            </Text>

            <Heading size="lg" color="main" mb={2}>Forum für verschiedene Meinungen</Heading>
            <Text mb={8}>
                Die «Wochen-Zeitung» stellt ein Forum dar, in dem Themen durch verschiedene Ansichten behandelt werden. 
                Sie ist politisch und religiös unabhängig. 
                Minderheiten sollen genauso zu Wort kommen wie Experten. 
                Dies mit dem Ziel, dass sich die Leserin und der Leser ein eigenes Bild machen können. 
                Die redaktionellen Beiträge konzentrieren sich ausschliesslich auf das lokale Geschehen. 
                Die fünfköpfige Redaktion, die das Kernteam bildet, wird von einem grossen Stamm Freier Mitarbeiterinnen und Mitarbeitern unterstützt. 
                Die Auflage der „Wochen-Zeitung“ stieg von anfänglich 18'000 auf heute {imprint?.circulation_number} Exemplare. 
                Wöchentlich lesen {imprint?.reader_number} Personen die Zeitung. 
                Im Lauf der Zeit wurde das Layout immer wieder angepasst. 
                Eine umfassende Überarbeitung erfuhr das Layout der „Wochen-Zeitung“ letztmals 2012. 
                Unter anderem wurden farbige Bilder eingeführt.
            </Text>

            <Flex mb={8} direction="column" alignItems="center">
                <Image src={zeitungen} w={{md: "70%"}} />
                <Text color="main" mt={2}>Wochen-Zeitung</Text>
            </Flex>

            <Heading size="lg" color="main" mb={2}>Elektronische Medien halten Einzug</Heading>
            <Text mb={4}>
                Seit der Gründung der «Wochen-Zeitung» 1980 hat sich die Nutzung der Medien gewandelt. 
                Das Internet und die crossmedialen Anwendungen haben Einzug gehalten – schliesslich lesen Sie diese Informationen über die 
                „Wochen-Zeitung“ via PC, Mac, Tablet oder Smartphone und nicht in der Zeitung. 
                Die Wochen-Zeitung ist seit 1997 im weltweiten Netz mit einem umfassenden Archiv präsent. 
                Unter <Link href="https://wochen-zeitung.ch" target="_blank" color="main" fontWeight="bold">wochen-zeitung.ch</Link> finden die Nutzer ein sehr gut ausgebautes Archiv, 
                in dem alle Artikel kostenlos gelesen werden können.
            </Text>
            <Text mb={8}>
                Im Sommer 2026 wurde die neue "Wochen-Zeitung" Webseite lanciert, um den Nutzern ein modernes und mobile taugliches Interface zu bieten. Die eigens entwickelte Webseite mit
                umfangreichem Redaktionssystem ermöglicht der Redaktion die Vereinfachte Erfassung der online verfügbaren Artikeln.
            </Text>

            <Heading size="lg" color="main" mb={2}>Herrmann Druck – bereits seit 1975</Heading>
            <Text mb={8}>
                Werner Herrmann gründete die Druckerei Herrmann, heute Herrmann AG, bereits im Jahr 1975. 
                Sie ist noch heute bekannt für die rasche und qualitativ hochwertige Herstellung von Privat-, Vereins- und Geschäftsdrucksachen. 
                Die Palette reicht vom Visitenkärtli über Festführer bis hin zu Poster. 
                Bei den Trauerdrucksachen wurde gar eine eigene Kollektion erarbeitet. 
                Der Leitsatz der Druckerei lautet: „Mir chöi mit Druck umga“. 
                Weitere Informationen: <Link href="https://herrmann-druck.ch" target="_blank" color="main" fontWeight="bold">herrmann-druck.ch</Link>
            </Text>

            <Heading size="lg" color="main" mb={2}>Tag der offenen Tür</Heading>
            <ReactPlayer 
                src="https://youtu.be/w8qfx_1mjCA"
                controls
                style={{ 
                    width: "100%", 
                    height: "auto", 
                    aspectRatio: "16/9",
                }}  
            />

            
            <SimpleGrid gap={20} columns={{base: 1, md: 3}} alignItems="center" mt={20}>
                <Link target="_blank" href="https://www.wochen-zeitung.ch/"><Image src={logo} /></Link>
                <Link target="_blank" href="https://www.herrmann-druck.ch/"><Image src={hdLogo} /></Link>
                <Link target="_blank" href="https://verlag-herrmann.ch/"><Image src={verlagLogo} /></Link>
            </SimpleGrid>
        </Container>
    )
}
